import {
  FloatingContext,
  FloatingFocusManager,
  FloatingList,
  UseFloatingReturn,
} from '@floating-ui/react'
import clsx from 'clsx'
import React, { forwardRef, ReactNode, useEffect, useRef, useState } from 'react'
import styles from './ComboboxBase.module.scss'
import { ComboboxContext, ComboboxContextValue } from './ComboboxContext'
import { TSizeAll } from 'src/types'
import { Input } from '../Input'
import { Empty } from '../Empty'

export interface CommonComboboxProps {
  placeholder?: ReactNode
  children: JSX.Element[]
  error?: boolean | string
  disabled?: boolean
  label?: ReactNode
  isTransparent?: boolean
  size?: TSizeAll
  defaultIsOpen?: boolean
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void
  style?: React.CSSProperties
  id: string
  showSearch: boolean
  searchPlaceholder?: string
  /**
   * The maximum height of the dropdown.
   * @default all available height
   * @example '200px'
   */
  dropdownMaxHeight?: string
  /**
   * Optional custom className
   */
  className?: string
  allowMultiple: boolean
}

export interface ComboboxBaseProps extends CommonComboboxProps {
  floatingStyles?: React.CSSProperties
  context: FloatingContext
  selectedChildren: ReactNode
  referenceProps: Record<string, unknown>
  floatingProps: Record<string, unknown>
  elementsRef: React.MutableRefObject<(HTMLElement | null)[]>
  labelsRef: React.MutableRefObject<(string | null)[]>
  selectContext: ComboboxContextValue
  isOpen: boolean
  refs: UseFloatingReturn['refs']
}

export const ComboboxBase = forwardRef<HTMLDivElement, ComboboxBaseProps>(
  (
    {
      label,
      error,
      disabled,
      isTransparent,
      size = 'md',
      isOpen,
      refs,
      floatingStyles,
      context,
      children,
      selectedChildren,
      referenceProps,
      floatingProps,
      elementsRef,
      labelsRef,
      selectContext,
      style,
      searchPlaceholder,
      className,
      showSearch,
      id,
    },
    ref,
  ) => {
    const searchInputRef = useRef<HTMLInputElement>(null)
    const [filteredChildren, setFilteredChildren] = useState<ReactNode[]>(children)
    const [hasNoSearchResults, setHasNoSearchResults] = useState(false)

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      selectContext.setSearchTerm(e.target.value.toLowerCase())
    }

    const handleClearSearch = () => {
      selectContext.setSearchTerm('')
    }

    useEffect(() => {
      if (!selectContext.searchTerm) {
        setFilteredChildren(children)
        setHasNoSearchResults(false)
        return
      }

      const handler = setTimeout(() => {
        const newFilteredChildren = React.Children.map(children, (child) => {
          if (
            child.props.id &&
            child.props.children?.toLowerCase().includes(selectContext.searchTerm.toLowerCase())
          ) {
            return React.cloneElement(child, { style: { display: '' } })
          } else {
            return React.cloneElement(child, { style: { display: 'none' } })
          }
        })
        const noResults = newFilteredChildren.every((child) => child.props.style.display === 'none')
        setFilteredChildren(newFilteredChildren)
        setHasNoSearchResults(noResults)
      }, 300)

      return () => {
        clearTimeout(handler)
      }
    }, [selectContext, children])

    useEffect(() => {
      if (!isOpen) {
        selectContext.setSearchTerm('')
      }
    }, [isOpen, selectContext])

    return (
      <div
        className={clsx('mui-select-wrapper', styles.wrapper, className)}
        ref={ref}
        style={style}
      >
        {label ? (
          <label
            className={clsx('mui-select-label', styles.label, { [styles.disabled]: disabled })}
            htmlFor={id}
          >
            {label}
          </label>
        ) : null}
        <div
          id={id}
          tabIndex={0}
          ref={refs.setReference}
          aria-labelledby="select-label"
          aria-autocomplete="none"
          aria-disabled={disabled}
          aria-invalid={Boolean(error)}
          className={clsx('mui-select-select', styles.select, styles[size], {
            [styles.disabled]: disabled,
            [styles.transparent]: isTransparent,
            [styles.error]: Boolean(error),
          })}
          {...referenceProps}
        >
          {selectedChildren}
          <Chevron isOpen={isOpen} />
        </div>
        {typeof error === 'string' ? (
          <span className={clsx(styles.errorMessage, { [styles.disabled]: disabled })}>
            {error}
          </span>
        ) : null}
        <ComboboxContext.Provider value={selectContext}>
          <FloatingFocusManager
            context={context}
            modal={true}
            disabled={!isOpen}
            initialFocus={searchInputRef}
            order={['reference', 'content']}
          >
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...floatingProps}
              className={clsx(
                'mui-select-dropdown',
                'drop-shadow-large-down-10',
                styles.dropdown,
                styles[size],
                {
                  [styles.closed]: !isOpen,
                },
              )}
            >
              {showSearch && (
                <div className={styles.searchInputHolder}>
                  <Input
                    size="sm"
                    id="combobox-search-input"
                    ref={searchInputRef}
                    value={selectContext.searchTerm}
                    onChange={(e) => handleSearchChange(e)}
                    onKeyDown={(e) => e.stopPropagation()}
                    slots={{
                      before: (
                        <svg xmlns="http://www.w3.org/2000/svg" height="1em" viewBox="0 0 512 512">
                          <path d="M368 208A160 160 0 1 0 48 208a160 160 0 1 0 320 0zM337.1 371.1C301.7 399.2 256.8 416 208 416C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208c0 48.8-16.8 93.7-44.9 129.1L505 471c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L337.1 371.1z" />
                        </svg>
                      ),
                      after: selectContext.searchTerm ? (
                        <div onClick={handleClearSearch} className={styles.closeButton}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                            <path d="M345 137c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-119 119L73 103c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l119 119L39 375c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l119-119L311 409c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-119-119L345 137z" />
                          </svg>
                        </div>
                      ) : null,
                    }}
                    placeholder={searchPlaceholder || 'Search by keyword'}
                  />
                </div>
              )}

              <ul className={clsx('mui-select-dropdown-list', styles.list)}>
                <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
                  {hasNoSearchResults ? (
                    <Empty
                      text={`No matching results`}
                      className={styles.comboboxEmpty}
                      icon={
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="1em"
                            viewBox="0 0 512 512"
                          >
                            <path d="M32 416V327.9c0-2.6 .3-5.2 1-7.8l0-.1H134.1l23.2 46.3c5.4 10.8 16.5 17.7 28.6 17.7H326.1c12.1 0 23.2-6.8 28.6-17.7L377.9 320H479l0 .1c.6 2.5 1 5.1 1 7.8V416c0 17.7-14.3 32-32 32H64c-17.7 0-32-14.3-32-32zM471 288H377.9c-12.1 0-23.2 6.8-28.6 17.7L326.1 352H185.9l-23.2-46.3c-5.4-10.8-16.5-17.7-28.6-17.7H41L90.9 88.2C94.5 74 107.3 64 122 64H390c14.7 0 27.5 10 31 24.2L471 288zM0 327.9V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V327.9c0-5.2-.6-10.4-1.9-15.5l-58-231.9C445 52 419.4 32 390 32H122C92.6 32 67 52 59.9 80.5L1.9 312.4C.6 317.4 0 322.6 0 327.9z" />
                          </svg>
                        </>
                      }
                    />
                  ) : (
                    filteredChildren
                  )}
                </FloatingList>
              </ul>
            </div>
          </FloatingFocusManager>
        </ComboboxContext.Provider>
      </div>
    )
  },
)

const Chevron = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    width="16"
    height="10"
    viewBox="0 0 16 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={clsx(styles.chevron, {
      [styles.open]: isOpen,
    })}
  >
    <path
      d="M7.28125 8.71875L1.28125 2.71875C0.875 2.34375 0.875 1.6875 1.28125 1.3125C1.65625 0.90625 2.3125 0.90625 2.6875 1.3125L8 6.59375L13.2812 1.3125C13.6562 0.90625 14.3125 0.90625 14.6875 1.3125C15.0938 1.6875 15.0938 2.34375 14.6875 2.71875L8.6875 8.71875C8.3125 9.125 7.65625 9.125 7.28125 8.71875Z"
      fill="#6286B7"
    />
  </svg>
)
